import { getAPI } from 'src/utils/api';
import checkAuthError from '../utils/checkAuthError';
import i18n from 'i18next';
import { notification } from 'antd';

notification.config({
	placement: 'topRight',
	top: 75,
	duration: 2,
});

const getViews = async (modelId) => {
	try {
		const api = await getAPI();
		const { data } = await api.get(`model/${modelId}/view/all`);
		const { views } = data;
		return views;
	} catch (error) {
		checkAuthError(error);

		if (error.response.data.message) {
			console.error(error.response.data.message);
			throw new Error(error.response.data.message);
		} else {
			console.error(error);
			throw error;
		}
	}
};

const addView = async (modelId, displayTitle, viewType) => {
	try {
		const api = await getAPI();
		const { data } = await api.post(`model/${modelId}/view`, {
			displayTitle,
			viewType,
		});
		const { view } = data;
		notification.success({
			message: i18n.t('notifications.ViewCreationSuccess'),
		});
		return view;
	} catch (error) {
		checkAuthError(error);

		if (error.response.data.message) {
			console.error(error.response.data.message);
			notification.error({
				message: i18n.t('notifications.ViewCreationError'),
				description: error.response.data.message,
			});
			throw new Error(error.response.data.message);
		} else {
			console.error(error);
			notification.error({
				message: i18n.t('notifications.ViewCreationError'),
				description: error,
			});
			throw error;
		}
	}
};

const updateView = async (modelId, viewId, viewData) => {
	try {
		const api = await getAPI();
		const { data } = await api.put(`model/${modelId}/view/${viewId}`, viewData);
		const { view } = data;
		notification.success({
			message: i18n.t('notifications.viewUpdateSuccess', 'View sucessfully updated'),
		});
		return view;
	} catch (error) {
		checkAuthError(error);

		if (error.response.data.message) {
			console.error(error.response.data.message);
			notification.error({
				message: i18n.t('notifications.viewUpdateError', 'View could not be updated'),
				description: error.response.data.message,
			});
			throw new Error(error.response.data.message);
		} else {
			console.error(error);
			notification.error({
				message: i18n.t('notifications.viewUpdateError', 'View could not be updated'),
				description: error,
			});
			throw error;
		}
	}
};

const updateProjectViewSettings = async (modelId, viewId, projectSettings) => {
	try {
		const api = await getAPI();
		const { data } = await api.put(`model/${modelId}/view/${viewId}/project/settings`, { projectSettings });
		const { view } = data;
		if (data.success)
			notification.success({
				message: i18n.t('notifications.viewUpdateSuccess', 'View sucessfully updated'),
			});
		return view;
	} catch (error) {
		checkAuthError(error);

		if (error.response.data.message) {
			console.error(error.response.data.message);
			notification.error({
				message: i18n.t('notifications.viewUpdateError', 'View could not be updated'),
				description: error.response.data.message,
			});
			throw new Error(error.response.data.message);
		} else {
			console.error(error);
			notification.error({
				message: i18n.t('notifications.viewUpdateError', 'View could not be updated'),
				description: error,
			});
			throw error;
		}
	}
};

const deleteView = async (modelId, viewId) => {
	try {
		const api = await getAPI();
		const { data } = await api.delete(`/model/${modelId}/view/${viewId}`);
		const { view } = data;
		notification.success({
			message: i18n.t('notifications.viewDeleteSuccess', 'View sucessfully deleted'),
		});
		return view;
	} catch (error) {
		checkAuthError(error);

		if (error.response.data.message) {
			console.error(error.response.data.message);
			notification.error({
				message: i18n.t('notifications.viewDeleteError', 'View could not be deleted'),
				description: error.response.data.message,
			});
			throw new Error(error.response.data.message);
		} else {
			console.error(error);
			notification.error({
				message: i18n.t('notifications.viewDeleteError', 'View could not be deleted'),
				description: error,
			});
			throw error;
		}
	}
};

const addLayout = async (modelId, viewId, title, viewType, layoutType, layoutData) => {
	try {
		const api = await getAPI();
		const { data } = await api.post(`/model/${modelId}/view/${viewId}/layout/${layoutType}`, {
			title,
			layoutData,
			viewType,
		});
		const { layout } = data;
		notification.success({
			message: i18n.t('notifications.LayoutCreationSuccess'),
		});
		return layout;
	} catch (error) {
		checkAuthError(error);

		if (error.response.data.message) {
			console.error(error.response.data.message);
			notification.error({
				message: i18n.t('notifications.LayoutCreationError'),
				description: error.response.data.message,
			});
			throw new Error(error.response.data.message);
		} else {
			console.error(error);
			notification.error({
				message: i18n.t('notifications.LayoutCreationError'),
				description: error,
			});
			throw error;
		}
	}
};

const deleteLayout = async (layoutId, layoutType) => {
	try {
		const api = await getAPI();
		const { data } = await api.delete(`layout/${layoutId}/${layoutType}`);
		const { layout } = data;
		notification.success({
			message: i18n.t('notifications.layoutDeleteSuccess', 'Layout sucessfully deleted'),
		});
		return layout;
	} catch (error) {
		checkAuthError(error);

		if (error.response.data.message) {
			console.error(error.response.data.message);
			notification.error({
				message: i18n.t('notifications.layoutDeleteError', 'Layout sucessfully deleted'),
				description: error.response.data.message,
			});
			throw new Error(error.response.data.message);
		} else {
			console.error(error);
			notification.error({
				message: i18n.t('notifications.layoutDeleteError', 'Layout sucessfully deleted'),
				description: error,
			});
			throw error;
		}
	}
};

const getAllViewElements = async (modelId, viewId, revision = undefined) => {
	try {
		const viewUrl = revision
			? `/model/${modelId}/view/${viewId}/children/all?revision=${revision}`
			: `/model/${modelId}/view/${viewId}/children/all`;

		const api = await getAPI();
		const { data } = await api.get(viewUrl);
		const { allElements, layeredElements } = data;
		return { allElements, viewId, layeredElements };
	} catch (error) {
		checkAuthError(error);
		console.log('error', error);
	}
};

const getExtendedViewElementsList = async (modelId, viewId, index, limit = 20) => {
	try {
		const viewUrl = `/model/${modelId}/view/${viewId}/children/extended/${index}/${limit}`;

		const api = await getAPI();
		const { data } = await api.get(viewUrl);
		return data;
	} catch (error) {
		checkAuthError(error);
		console.log('error', error);
	}
};

export {
	addView,
	getViews,
	updateView,
	updateProjectViewSettings,
	addLayout,
	deleteLayout,
	deleteView,
	getAllViewElements,
	getExtendedViewElementsList,
};
